import { QuotingPortal } from 'lib/hosts/quoting/interface';
import { Undef } from 'types/UtilityTypes';
import captureException from 'utils/captureException';

export const TransporeonSubmitAction = 'transporeon-submit-quote';

const OFFERS_HOST = 'offers.transporeon.com';

export function getTransporeonOfferTenderId(
  urlStr: Undef<string>
): Undef<string> {
  if (!urlStr) {
    return undefined;
  }

  try {
    const url = new URL(urlStr);
    if (url.hostname !== OFFERS_HOST) {
      return undefined;
    }

    return url.hash.match(
      /^#\/loads\/offers\/modal\/([^/]+)\/(?:dashboard|cargo-details)(?:\/|$)/
    )?.[1];
  } catch {
    return undefined;
  }
}

export function isTransporeonHost(urlStr: Undef<string>): boolean {
  if (!urlStr) {
    return false;
  }

  try {
    const url = new URL(urlStr);

    return (
      url.hostname === OFFERS_HOST ||
      url.hostname === 'login.transporeon.com' ||
      url.hostname.endsWith('.login.transporeon.com')
    );
  } catch {
    return false;
  }
}

export function isTransporeonSupportedUrl(urlStr: Undef<string>): boolean {
  if (!isTransporeonHost(urlStr)) {
    return false;
  }

  try {
    const url = new URL(urlStr!);
    if (url.hostname === OFFERS_HOST) {
      return (
        url.hash === '#/loads/offers' ||
        url.hash.startsWith('#/loads/offers?') ||
        getTransporeonOfferTenderId(urlStr) !== undefined
      );
    }

    const isSupportedHash =
      url.hash.includes('AssignedTransportsCarrier') ||
      url.hash.includes('AssignedDeliveriesCarrier') ||
      url.hash.includes('OffersCarrier') ||
      url.hash.includes('UnityStart');

    const isVisibilityPath = url.pathname.includes('/visibility/');

    return isSupportedHash || isVisibilityPath;
  } catch {
    return false;
  }
}

export class Transporeon implements QuotingPortal {
  submitAction = TransporeonSubmitAction;

  canSubmit(tab: Undef<chrome.tabs.Tab>, html: string): boolean {
    try {
      if (!getTransporeonOfferTenderId(tab?.url)) {
        return false;
      }

      const doc = new DOMParser().parseFromString(html, 'text/html');
      return Boolean(
        doc.querySelector(
          [
            '[data-testid="e2e-bid-button"]',
            '[data-testid="e2e-bid-value-input"]',
            '[data-testid="e2e-bid-confirm-button"]',
          ].join(',')
        )
      );
    } catch (error: unknown) {
      captureException(error, {
        context: 'Error checking whether Transporeon can submit a quote',
      });
      return false;
    }
  }
}
